import { Box, Text, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { theme } from '@resistapp/client/components/shared/theme';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { useAdminShipment, useCountries, usePatchAdminShipment } from '@resistapp/client/hooks/api';
import { useQueryClient } from '@tanstack/react-query';
import { omit } from 'lodash';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ShipmentForm, buildShipmentFormDefaults } from './shipment-form';

export function AdminShipmentEditView() {
  const params = useParams();
  const shipmentId = params.id ? Number(params.id) : undefined;
  const queryClient = useQueryClient();
  const showToast = useToastWithoutDebounce();

  const { data: shipment, isPending, error } = useAdminShipment(shipmentId);
  const { data: countriesByAlpha3 } = useCountries();
  const patchShipment = usePatchAdminShipment();

  const defaultValues = useMemo(() => {
    if (!shipment) {
      return null;
    }

    return buildShipmentFormDefaults({
      organisationId: shipment.organisationId,
      labWorkbookId: shipment.labWorkbookId,
      status: shipment.status,
      pickupDate: shipment.pickupDate,
      pickupWindowStart: shipment.pickupWindowStart,
      pickupWindowEnd: shipment.pickupWindowEnd,
      contactName: shipment.contactName,
      contactEmail: shipment.contactEmail,
      contactTelephoneNumber: shipment.contactTelephoneNumber,
      pickupCountry: shipment.pickupCountry,
      pickupAddressLine1: shipment.pickupAddressLine1,
      pickupAddressLine2: shipment.pickupAddressLine2,
      pickupAddressLine3: shipment.pickupAddressLine3,
      pickupPostalCode: shipment.pickupPostalCode,
      pickupCity: shipment.pickupCity,
      taxId: shipment.taxId,
      packageWeightKg: shipment.packageWeightKg,
      packageLengthCm: shipment.packageLengthCm,
      packageWidthCm: shipment.packageWidthCm,
      packageHeightCm: shipment.packageHeightCm,
      items: shipment.items.map(item => ({
        description: item.description,
        count: item.count,
      })),
    });
  }, [shipment]);

  if (isPending) {
    return <LoadingView />;
  }

  if (error) {
    return (
      <Box padding={theme.spacing[6]}>
        <MaybeApiError error={error} />
      </Box>
    );
  }

  if (!shipmentId) {
    return (
      <Box padding={theme.spacing[6]}>
        <Text>Shipment not found.</Text>
      </Box>
    );
  }

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title={`Edit Shipment #${shipmentId}`} $extraWide={true} />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <ShipmentForm
          mode="edit"
          defaultValues={defaultValues as ReturnType<typeof buildShipmentFormDefaults>}
          countriesByAlpha3={countriesByAlpha3}
          organisationOptions={[{ id: shipment.organisation.id, name: shipment.organisation.name }]}
          labWorkbookOptions={
            shipment.labWorkbook
              ? [{ id: shipment.labWorkbook.id, name: shipment.labWorkbook.name ?? 'Untitled workbook' }]
              : undefined
          }
          disableOrganisationSelect={true}
          disableLabWorkbookSelect={true}
          allowStatusEdit={true}
          submitLabel="Save shipment changes"
          isSubmitting={patchShipment.isPending}
          onSubmit={async values => {
            const patchBody = omit(values, ['organisationId', 'labWorkbookId']);
            await patchShipment.mutateAsync({
              shipmentId,
              body: patchBody,
            });
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: [`/admin/shipment/${shipmentId}`] }),
              queryClient.invalidateQueries({
                predicate: query =>
                  typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('/admin/shipments'),
              }),
              queryClient.invalidateQueries({
                predicate: query =>
                  typeof query.queryKey[0] === 'string' &&
                  query.queryKey[0].startsWith(`/admin/organisations/${shipment.organisation.id}/shipments`),
              }),
              queryClient.invalidateQueries({ queryKey: ['/shipping/workbooks'] }),
            ]);
            showToast({
              title: 'Shipment saved',
              description: `Shipment #${shipmentId} was updated.`,
              status: 'success',
              duration: 7000,
            });
          }}
        />
        <MaybeApiError error={patchShipment.error} />
      </VStack>
    </div>
  );
}
